import { z } from "zod";

/// Wire-shape mirrors of the shore Notifications DTOs. Per webui T3 / T4
/// every external boundary is Zod-validated. Spec §2.5: the categories are
/// a closed set; extending requires a coordinated DB enum + UI migration
/// (kept in lockstep with shore's NOTIFICATION_CATEGORIES).
export const NOTIFICATION_CATEGORIES = [
  "feedback",
  "comments",
  "mentions",
  "recognition",
  "team_recognition",
  "one_on_one",
  "todos",
  "pulse_insights",
  "pulse_surveys",
  "pulse_actions",
  "performance_reviews",
  "goals",
  "workspace_admin",
  "critical_safety_alerts",
  // Module 17, HRMS Leave. Filed applications notify approvers, decided
  // applications notify the applicant. Not always-on, matching the HR
  // categories below.
  "leave",
  // Module 00, HR foundation (FND-028). None of these are always-on: an HR
  // notification is never so urgent that a member may not opt out of it.
  "hr_requests",
  "hr_scheduling",
  "hr_payroll_self",
  "hr_updates",
  "hr_admin_ops",
  "hr_celebrations",
  "pulse_answer_replies",
] as const;
export const notificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = z.infer<typeof notificationCategorySchema>;

/// Always-on categories, preferences UI hides toggles for these. Mirror
/// of shore's `ALWAYS_ON_CATEGORIES` set; same single-source-of-truth rule.
export const ALWAYS_ON_CATEGORIES: ReadonlySet<NotificationCategory> = new Set([
  "critical_safety_alerts",
]);

/// FND-028b's shore migration landed the Prisma NotificationCategory enum
/// value for all six hr_* categories, so shore's DTOs now validate a
/// category filter against the full vocabulary and this set is empty:
/// every category above is filterable. Keep the set and
/// `isInboxFilterableCategory` rather than deleting them, this is the
/// mirror of shore's PERSISTED_NOTIFICATION_CATEGORIES / NOTIFICATION_CATEGORIES
/// split: anything that SENDS a category to shore (the inbox filter
/// dropdown, a future mark-all-read scope) must offer only what shore's
/// DTOs accept, and this is the seam for the next category that ships to
/// the vocabulary ahead of its shore migration. Removing it means the next
/// person reinvents it.
const CATEGORIES_NOT_YET_FILTERABLE: ReadonlySet<NotificationCategory> =
  new Set<NotificationCategory>([]);

export function isInboxFilterableCategory(category: NotificationCategory): boolean {
  return !CATEGORIES_NOT_YET_FILTERABLE.has(category);
}

const notificationUserSummarySchema = z
  .object({
    userId: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
  })
  .strict();
export type NotificationUserSummary = z.infer<typeof notificationUserSummarySchema>;

export const notificationRowSchema = z
  .object({
    id: z.string(),
    workspaceId: z.string(),
    recipientUserId: z.string(),
    category: notificationCategorySchema,
    eventName: z.string(),
    title: z.string(),
    message: z.string().nullable(),
    resourceType: z.string().nullable(),
    resourceId: z.string().nullable(),
    actorUserId: z.string().nullable(),
    actor: notificationUserSummarySchema.nullable(),
    aggregationCount: z.number().int(),
    aggregationActorIds: z.array(z.string()),
    isRead: z.boolean(),
    readAt: z.string().nullable(),
    emailQueued: z.boolean(),
    emailSentAt: z.string().nullable(),
    emailFailureReason: z.string().nullable(),
    outboxEventId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type NotificationRow = z.infer<typeof notificationRowSchema>;

export const listNotificationsResponseSchema = z
  .object({
    rows: z.array(notificationRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ListNotificationsResponse = z.infer<typeof listNotificationsResponseSchema>;

export const NOTIFICATION_STATUS_FILTERS = ["all", "unread", "read"] as const;
export const notificationStatusFilterSchema = z.enum(NOTIFICATION_STATUS_FILTERS);
export type NotificationStatusFilter = z.infer<typeof notificationStatusFilterSchema>;

export const listNotificationsQuerySchema = z
  .object({
    status: notificationStatusFilterSchema.optional(),
    category: notificationCategorySchema.optional(),
    since: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(50).optional(),
  })
  .strict();
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;

export const unreadCountResponseSchema = z.object({ count: z.number().int() }).strict();
export type UnreadCountResponse = z.infer<typeof unreadCountResponseSchema>;

export const markAllReadResponseSchema = z.object({ count: z.number().int() }).strict();
export type MarkAllReadResponse = z.infer<typeof markAllReadResponseSchema>;

export const okResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type OkResponse = z.infer<typeof okResponseSchema>;

export const notificationPreferenceRowSchema = z
  .object({
    category: notificationCategorySchema,
    displayName: z.string(),
    inAppEnabled: z.boolean(),
    emailEnabled: z.boolean(),
    isAlwaysOn: z.boolean(),
  })
  .strict();
export type NotificationPreferenceRow = z.infer<typeof notificationPreferenceRowSchema>;

export const listNotificationPreferencesResponseSchema = z
  .object({ rows: z.array(notificationPreferenceRowSchema) })
  .strict();
export type ListNotificationPreferencesResponse = z.infer<
  typeof listNotificationPreferencesResponseSchema
>;

export const updateNotificationPreferenceSchema = z
  .object({
    category: notificationCategorySchema,
    inAppEnabled: z.boolean(),
    emailEnabled: z.boolean(),
  })
  .strict();
export type UpdateNotificationPreference = z.infer<typeof updateNotificationPreferenceSchema>;

// 2026-05-21: delivery-audit admin surface removed.
